import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, HelpCircle } from "lucide-react";
import Seo from "@/components/common/Seo";
import { faqLd } from "@/lib/jsonld";

// Single source of truth for the FAQ content. Drives BOTH the visible Q&A and
// the FAQPage JSON-LD (raw plain-text answers) — never hardcoded twice.
const FAQS = [
  {
    question: "What is AutoCareX?",
    answer:
      "AutoCareX is an intelligent car care brand offering a full range of professional-grade DIY products — shampoos, waxes, and interior, wheel and AC cleaners — designed so everyday Indian car owners get detailing-quality results at home.",
  },
  {
    question: "Are AutoCareX products safe for all cars?",
    answer:
      "Yes. Our formulas are designed to be safe across common automotive paints, finishes, glass, plastics and trims. Always follow the usage instructions on each product label, and test on a small area first if you're unsure.",
  },
  {
    question: "Is the Car Shampoo safe over wax or ceramic coatings?",
    answer:
      "Yes. Our pH-balanced Car Shampoo cleans without stripping existing wax or ceramic protection, so your coating stays intact between washes.",
  },
  {
    question: "Do I need professional equipment to use AutoCareX products?",
    answer:
      "No. Every product is built for DIY use — a bucket, a sponge or our microfibre cloth, and water are enough for most jobs. You get professional-looking results without a workshop.",
  },
  {
    question: "What sizes and product options are available?",
    answer:
      "AutoCareX products come in a handy 250ml size and a larger 5L variant for heavy or frequent use, along with accessories and ready-to-use car care kits — so you can pick the format that suits how often you detail your car.",
  },
  {
    question: "How often should I wash and wax my car?",
    answer:
      "As a general guide, wash every 1–2 weeks and apply liquid wax every 6–8 weeks to keep paint protected and glossy. Heavy dust, monsoon conditions and open parking common in Indian cities may call for more frequent care.",
  },
  {
    question: "Are AutoCareX products certified?",
    answer:
      "Yes. AutoCareX products meet recognised quality and environmental standards, including ISO 9001:2015, with CPCB and UPPCB compliance. The full, current certification details are listed on autocarex.in and jtmax.in.",
  },
  {
    question: "Do you deliver across India?",
    answer:
      "Yes, AutoCareX delivers pan-India. You'll find delivery timelines and coverage on our Delivery & Return Policy page.",
  },
  {
    question: "Where can I buy AutoCareX products?",
    answer:
      "You can buy AutoCareX directly at autocarex.in, and on Amazon and Flipkart.",
  },
  {
    question: "Do you offer returns or refunds?",
    answer:
      "Yes. AutoCareX has a returns and refund policy covering eligible orders — see our Delivery & Return Policy page for eligibility, timelines and the full process.",
  },
];

// Visual-only helper: turn the literal phrase "Delivery & Return Policy" into an
// internal client-side router link. Affects rendered output only, not the JSON-LD.
const POLICY_PHRASE = "Delivery & Return Policy";
function renderAnswer(answer) {
  if (!answer.includes(POLICY_PHRASE)) return answer;
  const parts = answer.split(POLICY_PHRASE);
  const nodes = [];
  parts.forEach((part, i) => {
    if (i > 0) {
      nodes.push(
        <Link
          key={`policy-${i}`}
          to="/delivery-return-policy"
          className="text-white underline decoration-[#E11B22]/60 underline-offset-2 transition-colors hover:decoration-[#E11B22]"
        >
          {POLICY_PHRASE}
        </Link>
      );
    }
    if (part) nodes.push(<span key={`text-${i}`}>{part}</span>);
  });
  return nodes;
}

export default function Faq() {
  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo
        title="Car Care FAQs – Usage, Safety & Delivery"
        description="Answers to common questions about AutoCareX car care products — how to use them, safety on paint and coatings, certifications and delivery across India."
        jsonLd={faqLd(FAQS.map((f) => ({ q: f.question, a: f.answer })))}
      />

      {/* Hero */}
      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_25%_0%,rgba(156,163,175,0.10),transparent_55%),radial-gradient(640px_circle_at_85%_10%,rgba(225,27,34,0.10),transparent_55%)]" />
        <div className="acx-container relative px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <nav className="flex items-center gap-2 text-[13px] text-white/45" aria-label="Breadcrumb">
              <Link to="/" className="transition-colors hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <span className="text-white/70">FAQ</span>
            </nav>
            <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3.5 py-1.5 text-[12px] uppercase tracking-[0.18em] text-white/55">
              <HelpCircle size={14} className="text-[#E11B22]" /> FAQ
            </span>
            <h1 className="mt-4 max-w-3xl font-display text-[34px] font-extrabold leading-[1.06] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
              Frequently asked questions
            </h1>
            <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-white/60">
              Everything you need to know about AutoCareX — products, safety, certifications and delivery across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[var(--acx-bg)]">
        <div className="acx-container px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
          <article className="min-w-0 max-w-3xl" data-testid="faq-list">
            {FAQS.map((item, i) => (
              <section
                key={item.question}
                data-testid={`faq-item-${i}`}
                className="scroll-mt-28 border-t border-[color:var(--acx-border)] py-8 first:border-t-0 first:pt-0"
              >
                <h2 className="font-display text-[20px] font-bold tracking-[-0.02em] text-white sm:text-[22px]">
                  {item.question}
                </h2>
                <p className="mt-3 text-[15px] leading-[1.75] text-white/65">
                  {renderAnswer(item.answer)}
                </p>
              </section>
            ))}
          </article>
        </div>
      </section>
    </div>
  );
}
